import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { api } from '../../../shared/api';
import type { ExamType, Mark, Subject } from '../../../shared/types';
import PageHeader from '../../../shared/PageHeader';
import { formatDate, gradeFromPercentage } from '../../../shared/utils';
import { useAuthStore } from '../../../features/auth/store';
import { SCHOOL } from '../../../shared/constants';

export default function MarksheetPage() {
  const user = useAuthStore((s) => s.user);
  const marksQ = useQuery({ queryKey: ['marks-me'], queryFn: async () => (await api.get<Mark[]>('/marks')).data });
  const examQ = useQuery({ queryKey: ['exam-types'], queryFn: async () => (await api.get<ExamType[]>('/exam-types')).data });
  const subjQ = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<Subject[]>('/subjects')).data });

  const subjMap = useMemo(() => new Map((subjQ.data ?? []).map((s) => [s.id, s.name])), [subjQ.data]);
  const printedExams = useMemo(() => {
    const grouped: Record<string, Mark[]> = {};
    (marksQ.data ?? []).forEach((m) => (grouped[m.examTypeId] ??= []).push(m));
    return (examQ.data ?? []).filter((ex) => grouped[ex.id]?.length).map((ex) => ({ ex, list: grouped[ex.id] }));
  }, [marksQ.data, examQ.data]);

  const printArea = useRef<HTMLDivElement>(null);

  const handlePrint = (examName: string) => {
    if (!printArea.current) return;
    const html = printArea.current.querySelector(`[data-exam="${examName}"]`)?.outerHTML;
    if (!html) return;
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Marksheet — ${examName}</title>
      <style>
        body{font-family:Inter,system-ui,sans-serif;padding:32px;color:#0F172A}
        table{width:100%;border-collapse:collapse;margin-top:16px}
        th,td{border:1px solid #CBD5E1;padding:8px;text-align:left;font-size:13px}
        th{background:#F1F5F9}
        .head{display:flex;align-items:center;gap:16px;border-bottom:3px double #1E3A8A;padding-bottom:16px}
        .head h1{margin:0;font-size:24px;color:#1E3A8A}
        .head .sub{color:#475569;font-size:13px}
        .meta{display:flex;justify-content:space-between;margin-top:16px;font-size:13px}
        .total{margin-top:16px;background:#F1F5F9;padding:12px;border-radius:8px;display:flex;justify-content:space-between;font-weight:700}
        @media print {.no-print{display:none}}
      </style></head><body>${html}<div class="no-print" style="margin-top:24px;text-align:center"><button onclick="window.print()">Print</button></div></body></html>`);
    w.document.close();
  };

  return (
    <Box>
      <PageHeader title="Marksheets" subtitle="Download or print your published marksheets." />
      <Grid container spacing={2}>
        {printedExams.map(({ ex, list }) => {
          const total = list.reduce((s, m) => s + (m.marksObtained ?? 0), 0);
          const max = list.reduce((s, m) => s + m.maxMarks, 0);
          const pct = max ? +(total / max * 100).toFixed(2) : 0;
          return (
            <Grid item xs={12} md={6} key={ex.id}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{ex.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {list.length} subjects · {total} / {max} ({pct}%)
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip color="primary" label={gradeFromPercentage(pct)} />
                      <Button startIcon={<Print />} variant="outlined" size="small" onClick={() => handlePrint(ex.name)}>Print</Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {printedExams.length === 0 && (
          <Grid item xs={12}><Card><CardContent sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary">No published marksheets yet.</Typography></CardContent></Card></Grid>
        )}
      </Grid>

      {/* Hidden printable templates */}
      <Box ref={printArea} sx={{ display: 'none' }}>
        {printedExams.map(({ ex, list }) => {
          const total = list.reduce((s, m) => s + (m.marksObtained ?? 0), 0);
          const max = list.reduce((s, m) => s + m.maxMarks, 0);
          const pct = max ? +(total / max * 100).toFixed(2) : 0;
          return (
            <div key={ex.id} data-exam={ex.name}>
              <div className="head">
                <div style={{ width: 56, height: 56, background: '#1E3A8A', borderRadius: 12, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800 }}>B</div>
                <div>
                  <h1>{SCHOOL.name}</h1>
                  <div className="sub">{SCHOOL.address}</div>
                  <div className="sub" style={{ fontWeight: 700, color: '#1E3A8A', marginTop: 4 }}>Marksheet · {ex.name} · 2025-2026</div>
                </div>
              </div>
              <div className="meta">
                <div><b>Student:</b> {user?.fullName}</div>
                <div><b>Roll:</b> {user?.rollNumber}</div>
                <div><b>Issued:</b> {formatDate(new Date().toISOString())}</div>
              </div>
              <table>
                <thead><tr><th>Subject</th><th style={{ textAlign: 'right' }}>Marks</th><th style={{ textAlign: 'right' }}>Max</th><th style={{ textAlign: 'right' }}>%</th><th style={{ textAlign: 'right' }}>Grade</th></tr></thead>
                <tbody>
                  {list.map((m) => {
                    const p = m.maxMarks ? +(((m.marksObtained ?? 0) / m.maxMarks) * 100).toFixed(1) : 0;
                    return (
                      <tr key={m.id}>
                        <td>{subjMap.get(m.subjectId)}</td>
                        <td style={{ textAlign: 'right' }}>{m.marksObtained ?? '—'}</td>
                        <td style={{ textAlign: 'right' }}>{m.maxMarks}</td>
                        <td style={{ textAlign: 'right' }}>{p}%</td>
                        <td style={{ textAlign: 'right' }}>{gradeFromPercentage(p)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="total">
                <span>Grand Total</span>
                <span>{total} / {max} · {pct}% · Grade {gradeFromPercentage(pct)}</span>
              </div>
              <div style={{ marginTop: 60, display: 'flex', justifyContent: 'space-between' }}>
                <div>____________________<br />Class Teacher</div>
                <div>____________________<br />Principal</div>
              </div>
            </div>
          );
        })}
      </Box>
    </Box>
  );
}
